<template>
  <div class="extensions-manager">
    <!-- Upload area -->
    <div class="upload-area mb-4">
      <b-tabs v-model="uploadMode" content-class="pt-3" nav-class="upload-tabs" small>
        <b-tab :title="$t('extensions.uploadFile')" active>
          <div class="d-flex align-items-center">
            <b-form-file
              v-model="uploadFile"
              accept=".json"
              :placeholder="$t('extensions.chooseExtensionFile')"
              :drop-placeholder="$t('extensions.dropExtensionFile')"
              class="flex-grow-1 mr-2"
            ></b-form-file>
            <b-button
              variant="primary"
              size="sm"
              @click="uploadExtension"
              :disabled="!uploadFile"
            >
              <b-icon icon="upload" class="mr-1"></b-icon>
              {{ $t('extensions.uploadExtension') }}
            </b-button>
          </div>
        </b-tab>
        <b-tab :title="$t('extensions.pasteJson')">
          <b-form-textarea
            v-model="pastedJson"
            :placeholder="$t('extensions.pasteExtensionJson')"
            rows="6"
            max-rows="12"
            class="mb-2"
          ></b-form-textarea>
          <b-button
            variant="primary"
            size="sm"
            @click="uploadExtensionFromPaste"
            :disabled="!pastedJson"
          >
            <b-icon icon="plus-circle" class="mr-1"></b-icon>
            {{ $t('extensions.addExtension') }}
          </b-button>
        </b-tab>
      </b-tabs>
    </div>

    <!-- Extension cards -->
    <div v-if="extensions && extensions.length > 0" class="extensions-grid">
      <div v-for="(extension, index) in extensions" :key="extension.name" class="extension-card" :class="{ 'extension-inactive': !extension.is_active }">
        <div class="extension-header">
          <div class="extension-title">
            <b-icon icon="puzzle" class="mr-2 text-muted"></b-icon>
            <strong>{{ extension.name }}</strong>
            <b-badge :variant="extension.is_active ? 'success' : 'secondary'" pill class="ml-2">
              {{ extension.is_active ? $t('active') : $t('inactive') }}
            </b-badge>
          </div>
          <b-dropdown size="sm" variant="link" no-caret right toggle-class="text-muted p-0">
            <template #button-content>
              <b-icon icon="three-dots-vertical"></b-icon>
            </template>
            <b-dropdown-item @click="toggleExtensionStatus(index)">
              <b-icon :icon="extension.is_active ? 'pause-circle' : 'play-circle'" class="mr-2"></b-icon>
              {{ extension.is_active ? $t('deactivate') : $t('activate') }}
            </b-dropdown-item>
            <b-dropdown-item @click="viewExtensionDetails(extension)">
              <b-icon icon="eye" class="mr-2"></b-icon>
              {{ $t('extensions.viewDetails') }}
            </b-dropdown-item>
            <b-dropdown-item @click="openInExtensionBuilder(extension)">
              <b-icon icon="pencil-square" class="mr-2"></b-icon>
              {{ $t('extensions.openInBuilder') }}
            </b-dropdown-item>
            <b-dropdown-item @click="exportExtension(extension)">
              <b-icon icon="download" class="mr-2"></b-icon>
              {{ $t('extensions.exportExtension') }}
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item variant="danger" @click="confirmDeleteExtension(index)">
              <b-icon icon="trash" class="mr-2"></b-icon>
              {{ $t('delete') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <div class="extension-meta">
          <span class="meta-item">
            <small class="text-muted">v{{ extension.version }}</small>
          </span>
          <span class="meta-item">
            <b-icon icon="columns-gap" font-scale="0.85" class="text-muted mr-1"></b-icon>
            <small>{{ extension.fields ? extension.fields.length : 0 }} {{ $t('fields') }}</small>
          </span>
          <span class="meta-item">
            <b-icon icon="code-slash" font-scale="0.85" class="text-muted mr-1"></b-icon>
            <small>{{ extension.functions ? extension.functions.length : 0 }} {{ $t('functions.title') }}</small>
          </span>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="empty-state">
      <b-icon icon="puzzle" font-scale="2.5" class="text-muted mb-3"></b-icon>
      <p class="text-muted mb-1">{{ $t('extensions.noExtensionsInstalled') }}</p>
      <small class="text-muted">{{ $t('extensions.uploadExtensionToGetStarted') }}</small>
    </div>

    <!-- Extension Details Modal -->
    <main-dialog :show="detailsModal.show" size="lg" :title="detailsModal.extension ? detailsModal.extension.name : ''" @close="detailsModal.show = false">
      <div v-if="detailsModal.extension">
        <h6 class="mb-3">{{ $t('basicInformation') }}</h6>
        <table class="table table-sm table-borderless">
          <tbody>
            <tr>
              <td class="text-muted" style="width: 140px">{{ $t('name') }}</td>
              <td>{{ detailsModal.extension.name }}</td>
            </tr>
            <tr>
              <td class="text-muted">{{ $t('version') }}</td>
              <td>{{ detailsModal.extension.version }}</td>
            </tr>
            <tr>
              <td class="text-muted">{{ $t('status') }}</td>
              <td>
                <b-badge :variant="detailsModal.extension.is_active ? 'success' : 'secondary'" pill>
                  {{ detailsModal.extension.is_active ? $t('active') : $t('inactive') }}
                </b-badge>
              </td>
            </tr>
            <tr>
              <td class="text-muted">{{ $t('extensions.installedBy') }}</td>
              <td>{{ detailsModal.extension.installed_by }}</td>
            </tr>
            <tr>
              <td class="text-muted">{{ $t('extensions.installedAt') }}</td>
              <td>{{ formatDate(detailsModal.extension.installed_at) }}</td>
            </tr>
          </tbody>
        </table>

        <h6 class="mb-3 mt-4">{{ $t('fields') }} ({{ detailsModal.extension.fields ? detailsModal.extension.fields.length : 0 }})</h6>
        <div v-if="detailsModal.extension.fields && detailsModal.extension.fields.length > 0" class="detail-list">
          <div v-for="field in detailsModal.extension.fields" :key="field.key" class="detail-list-item">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ field.key }}</strong>
                <br>
                <small class="text-muted">{{ field.module }}.{{ field.type }}</small>
              </div>
              <b-badge variant="info" pill>{{ field.schema || 'default' }}</b-badge>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small">{{ $t('extensions.noFields') }}</p>

        <h6 class="mb-3 mt-4">{{ $t('functions.title') }} ({{ detailsModal.extension.functions ? detailsModal.extension.functions.length : 0 }})</h6>
        <div v-if="detailsModal.extension.functions && detailsModal.extension.functions.length > 0" class="detail-list">
          <div v-for="func in detailsModal.extension.functions" :key="func.name" class="detail-list-item">
            <strong>{{ func.name }}</strong>
            <pre class="mt-2 mb-0"><code>{{ func.expression }}</code></pre>
          </div>
        </div>
        <p v-else class="text-muted small">{{ $t('extensions.noFunctions') }}</p>

        <h6 class="mb-3 mt-4">{{ $t('extensions.configuration') }}</h6>
        <pre v-if="detailsModal.extension.config"><code>{{ JSON.stringify(detailsModal.extension.config, null, 2) }}</code></pre>
        <p v-else class="text-muted small">{{ $t('extensions.noConfiguration') }}</p>
      </div>

      <template #footer>
        <b-button variant="secondary" size="sm" @click="detailsModal.show = false">{{ $t('close') }}</b-button>
      </template>
    </main-dialog>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'Extensions',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    component: {
      type: Object,
      required: true
    },
    componentRef: {
      type: String,
      required: true
    },
    componentVersion: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      uploadFile: null,
      uploadMode: 0,
      pastedJson: '',
      detailsModal: {
        show: false,
        extension: null
      }
    }
  },
  computed: {
    extensions: {
      get() {
        return this.value || []
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  methods: {
    async uploadExtension() {
      if (!this.uploadFile) return

      try {
        const fileContent = await this.readFileAsText(this.uploadFile)
        this.processExtension(fileContent)
        this.uploadFile = null
      } catch (error) {
        console.error('Error reading file:', error)
        this.$bvToast.toast(this.$t('extensions.failedToUploadExtension'), {
          title: this.$t('error'),
          variant: 'danger'
        })
      }
    },

    uploadExtensionFromPaste() {
      if (!this.pastedJson) return

      this.processExtension(this.pastedJson)
      this.pastedJson = ''
    },

    processExtension(jsonContent) {
      try {
        const extension = JSON.parse(jsonContent)
        
        console.log('Parsed extension:', extension)

        // Validate extension structure
        if (!extension.name) {
          console.error('Extension validation failed. Name:', extension.name)
          this.$bvToast.toast(this.$t('extensions.invalidExtensionFile') + ' - Missing name', {
            title: this.$t('error'),
            variant: 'danger'
          })
          return
        }

        if (!/^[a-z0-9_]+$/.test(extension.name)) {
          this.$bvToast.toast(this.$t('extensionNameRules'), {
            title: this.$t('error'),
            variant: 'danger'
          })
          return
        }

        // Check if extension already exists
        const existingIndex = this.extensions.findIndex(ext => ext.name === extension.name)
        if (existingIndex !== -1) {
          this.$mainDialog.msgBoxConfirm(
            this.$t('extensions.extensionAlreadyExists', { name: extension.name }),
            {
              title: this.$t('extensions.confirmReplace'),
              okVariant: 'warning',
              okTitle: this.$t('replace'),
              cancelTitle: this.$t('cancel')
            }
          ).then(confirmed => {
            if (confirmed) {
              this.updateExtensionAPI(extension.name, extension)
            }
          })
        } else {
          this.createExtensionAPI(extension)
        }
      } catch (error) {
        console.error('Error parsing extension JSON:', error)
        console.error('JSON content:', jsonContent)
        this.$bvToast.toast(this.$t('extensions.invalidExtensionFile') + ' - ' + error.message, {
          title: this.$t('error'),
          variant: 'danger'
        })
      }
    },

    async createExtensionAPI(extension) {
      const popup = this.$loadingPopup()
      
      try {
        const response = await this.$http.post(`/v4/system/component/${this.componentRef}/${this.componentVersion}/extensions`, extension)
        
        // Use the returned settings to update extensions
        if (response.data && response.data.extensions) {
          this.$emit('input', response.data.extensions)
        }
        
        this.$bvToast.toast(this.$t('extensions.extensionUploaded'), {
          title: this.$t('success'),
          variant: 'success'
        })
      } catch (error) {
        console.error('Error creating extension:', error)
        const message = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : this.$t('extensions.failedToUploadExtension')
        
        this.$bvToast.toast(message, {
          title: this.$t('error'),
          variant: 'danger'
        })
      } finally {
        popup.hide()
      }
    },

    async updateExtensionAPI(name, extension) {
      const popup = this.$loadingPopup()
      
      try {
        const response = await this.$http.patch(`/v4/system/component/${this.componentRef}/${this.componentVersion}/extensions/${name}`, extension)
        
        // Use the returned settings to update extensions
        if (response.data && response.data.extensions) {
          this.$emit('input', response.data.extensions)
        }
        
        this.$bvToast.toast(this.$t('extensions.extensionUpdated'), {
          title: this.$t('success'),
          variant: 'success'
        })
      } catch (error) {
        console.error('Error updating extension:', error)
        const message = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : this.$t('extensions.failedToUploadExtension')
        
        this.$bvToast.toast(message, {
          title: this.$t('error'),
          variant: 'danger'
        })
      } finally {
        popup.hide()
      }
    },

    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = (e) => reject(e)
        reader.readAsText(file)
      })
    },

    async toggleExtensionStatus(index) {
      const extension = this.extensions[index]
      const newStatus = !extension.is_active
      const popup = this.$loadingPopup()

      try {
        const response = await this.$http.patch(`/v4/system/component/${this.componentRef}/${this.componentVersion}/extensions/${extension.name}`, {
          is_active: newStatus
        })

        // Use the returned settings to update extensions
        if (response.data && response.data.extensions) {
          this.$emit('input', response.data.extensions)
        }

        const status = newStatus ? 'activated' : 'deactivated'
        this.$bvToast.toast(
          this.$t('extensions.extensionStatusChanged', { name: extension.name, status: this.$t('extensions.' + status) }),
          {
            title: this.$t('success'),
            variant: 'success'
          }
        )
      } catch (error) {
        console.error('Error toggling extension status:', error)
        const message = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : this.$t('extensions.failedToUploadExtension')
        
        this.$bvToast.toast(message, {
          title: this.$t('error'),
          variant: 'danger'
        })
      } finally {
        popup.hide()
      }
    },

    confirmDeleteExtension(index) {
      const extension = this.extensions[index]
      this.$mainDialog.msgBoxConfirm(
        this.$t('extensions.confirmDeleteExtension', { name: extension.name }),
        {
          title: this.$t('extensions.confirmDeletion'),
          okVariant: 'danger',
          okTitle: this.$t('delete'),
          cancelTitle: this.$t('cancel')
        }
      ).then(confirmed => {
        if (confirmed) {
          this.deleteExtension(index)
        }
      })
    },

    async deleteExtension(index) {
      const extension = this.extensions[index]
      const popup = this.$loadingPopup()

      try {
        const response = await this.$http.delete(`/v4/system/component/${this.componentRef}/${this.componentVersion}/extensions/${extension.name}`)

        // Use the returned settings to update extensions
        if (response.data && response.data.settings && response.data.settings.extensions) {
          this.$emit('input', response.data.settings.extensions)
        }

        this.$bvToast.toast(this.$t('extensions.extensionDeleted', { name: extension.name }), {
          title: this.$t('success'),
          variant: 'success'
        })
      } catch (error) {
        console.error('Error deleting extension:', error)
        const message = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : this.$t('extensions.failedToUploadExtension')
        
        this.$bvToast.toast(message, {
          title: this.$t('error'),
          variant: 'danger'
        })
      } finally {
        popup.hide()
      }
    },

    viewExtensionDetails(extension) {
      this.detailsModal.extension = extension
      this.detailsModal.show = true
    },

    exportExtension(extension) {
      const extensionData = JSON.stringify(extension, null, 2)
      const blob = new Blob([extensionData], { type: 'application/json' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${extension.name}_${extension.version}.json`
      link.click()
      URL.revokeObjectURL(link.href)

      this.$bvToast.toast(this.$t('extensions.extensionExported', { name: extension.name }), {
        title: this.$t('success'),
        variant: 'success'
      })
    },

    openInExtensionBuilder(extension) {
      // Store extension in localStorage to import in builder
      localStorage.setItem('extensionBuilderImport', JSON.stringify(extension))
      
      // Navigate to extension builder
      this.$router.push('/admin/developer/extension-builder')
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('YYYY-MM-DD HH:mm:ss')
    }
  }
}
</script>

<style lang="scss" scoped>
.extensions-manager {
  .upload-area {
    background: var(--bg-card-secondary, #383a40);
    border-radius: 6px;
    padding: 1rem;
    border: 1px dashed var(--border-default, rgba(102, 126, 234, 0.12));

    ::v-deep .upload-tabs {
      border-bottom: 1px solid var(--border-default, rgba(102, 126, 234, 0.12));
      
      .nav-link {
        font-size: 0.85rem;
        padding: 0.4rem 0.75rem;
        color: var(--text-secondary, #b5bac1);
        
        &.active {
          background: var(--bg-card-secondary, #383a40);
          border-color: var(--border-default, rgba(102, 126, 234, 0.12)) var(--border-default, rgba(102, 126, 234, 0.12)) var(--bg-card-secondary, #383a40);
          color: var(--text-primary, #f2f3f5);
        }
      }
    }

    ::v-deep .tab-content {
      background: transparent;
      border: none;
    }

    ::v-deep .custom-file-label {
      background: var(--form-bg, #383a40);
      border-color: var(--form-border, #4e5058);
      color: var(--text-secondary, #b5bac1);

      &::after {
        background: var(--form-bg, #383a40);
        border-color: var(--form-border, #4e5058);
        color: var(--text-primary, #f2f3f5);
      }
    }
  }

  .extensions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 0.75rem;
  }

  .extension-card {
    background: var(--bg-card-secondary, #383a40);
    border-radius: 6px;
    padding: 0.875rem 1rem;
    border: 1px solid var(--border-default, rgba(102, 126, 234, 0.12));
    transition: border-color 0.15s ease;

    &:hover {
      border-color: var(--border-active, #6c80ff);
    }

    &.extension-inactive {
      opacity: 0.6;
    }

    .extension-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.6rem;
    }

    .extension-title {
      display: flex;
      align-items: center;
      font-size: 0.9rem;
      min-width: 0;
      
      strong {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .extension-meta {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
      
      .meta-item {
        display: inline-flex;
        align-items: center;
        color: var(--text-muted, #6c757d);
        font-size: 0.8rem;
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 3rem 1rem;
  }

  .detail-list {
    .detail-list-item {
      padding: 0.6rem 0;
      border-bottom: 1px solid var(--border-color, #dee2e6);

      &:last-child {
        border-bottom: none;
      }
    }
  }

  pre {
    background: var(--body-bg, #f8f9fa);
    padding: 0.75rem;
    border-radius: 4px;
    font-size: 0.8rem;
    overflow-x: auto;
    border: none;
  }
}
</style>
